/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { capitalizeEachWord } from '@ibm-watson/shared/modules/ia/util/string';
import { Language } from '../../../ts/skill';
import {
  DEFAULT_ARABIC_SPEECH_TO_TEXT_MODEL,
  DEFAULT_CZECH_SPEECH_TO_TEXT_MODEL,
  DEFAULT_DUTCH_SPEECH_TO_TEXT_MODEL,
  DEFAULT_ENGLISH_SPEECH_TO_TEXT_MODEL,
  DEFAULT_FRENCH_SPEECH_TO_TEXT_MODEL,
  DEFAULT_GERMAN_SPEECH_TO_TEXT_MODEL,
  DEFAULT_ITALIAN_SPEECH_TO_TEXT_MODEL,
  DEFAULT_JAPANESE_SPEECH_TO_TEXT_MODEL,
  DEFAULT_KOREAN_SPEECH_TO_TEXT_MODEL,
  DEFAULT_SPANISH_SPEECH_TO_TEXT_MODEL,
  DEFAULT_ENGLISH_SPEECH_TO_TEXT_MODEL_LSM
} from '../components/phone/constants';
import { isPremiumSpeech } from './speech-plan-helpers';

// used temporarily to test CREATION
// Setting to true means you MUST find instances that start with PREFERRED_PREFIX
// Test CREATE by setting to true and changing the PREFERRED_PREFIX to ssomething unique.
// or we will offer to create them.
const PREFERRED_PATTERN_ONLY = false;
const PREFERRED_PREFIX = 'WA';

/**
 * Retrieve a debug prefix to use for testing creation and filtering
 * @returns {string} debug prefix or 'undefined'
 */
const speechDebugPrefix = () => {
  return (
    window.conversation &&
    typeof window.conversation.speechDebugPrefix !== 'undefined' &&
    window.conversation.speechDebugPrefix
  );
};

export const parseVoice = voiceDescription => {
  if (!voiceDescription) {
    // Return nulls for all if not set.
    return {
      persona: null,
      englishDescription: null,
      localeDescription: null
    };
  }
  const [persona, description] = voiceDescription
    .split(' female')[0]
    .split(' male')[0]
    .split(': ');
  const parsedDescription = description.split('(');
  const englishDescription = parsedDescription[0].trim();
  const localeDescription = parsedDescription[1]
    ? parsedDescription[1].trim().replace(')', '')
    : null;
  return {
    persona,
    englishDescription,
    localeDescription: capitalizeEachWord(localeDescription)
  };
};

/**
 * Check if name is a preferred instance.
 * A preferred instance starts with the PREFERRED_PREFIX and a -
 *
 * @param {string} instanceName Name of instance
 * @returns {boolean} If instance is preferred
 */
const isPreferredName = instanceName => {
  const testValue = speechDebugPrefix() || PREFERRED_PREFIX;
  return instanceName?.startsWith(`${testValue}-`);
};

/**
 * Value instance for sorting by:
 * 1. same account && resource group,
 * 2. same account,
 * 3. prefix
 * 4. available
 *
 * @param {*} instance instance to value
 * @param {*} accountId  Currnet Account guid
 * @param {*} resourceGroupId Current ResourceGroup guid
 * @returns {integer} Value of instance for sorting
 */
const valueInstance = (instance, accountId, resourceGroupId) => {
  // Account has highest priority
  const account =
    accountId && accountId === instance?.entity?.account_id ? 10000 : 0;
  // Since isPremiumSpeech returns boolean, +true becomes 1 and +false becomes 0
  const premium = +isPremiumSpeech(instance) * 1000;
  // Then Resourcegroup
  const resourceGroup =
    resourceGroupId && resourceGroupId === instance?.entity?.resource_group_id
      ? 100
      : 0;
  // Then preferred name
  const preferred = isPreferredName(instance?.entity?.name) * 10;
  // Then available
  const available = instance.available * 1;
  return account + premium + resourceGroup + preferred + available;
};

export const buildInstanceSorter =
  ({ accountId, resourceGroupId }) =>
  (a, b) => {
    // Must be in same region
    const value =
      valueInstance(b, accountId, resourceGroupId) -
      valueInstance(a, accountId, resourceGroupId);
    if (value === 0) {
      const nameA = a?.name || a?.entity?.name;
      const nameB = b?.name || b?.entity?.name;
      return nameB.localeCompare(nameA);
    }
    return value;
  };
/**
 * Build preferredFilter based on information about WA
 * @param {*} params Object
 * @param {*} params.accountId AccountId of WA
 * @param {*} params.regionName regionName where WA is installed
 *
 * @returns {function} FilterFunction
 */
export const buildPreferredFilter =
  ({ accountId, regionName }) =>
  instance => {
    const hyphenRegex = /-/gi;
    const hasPreferredName = isPreferredName(instance?.entity?.name);
    const isUsableInstance =
      // Must be available or provisioning to be in list.
      (instance?.available || instance?.state === 'provisioning') &&
      // Must be in same region
      instance?.region === regionName &&
      // must be in same account
      // sometimes AccountIds have hyphens and sometimes they dont...
      instance?.entity?.account_id.replace(hyphenRegex, '') ===
        accountId.replace(hyphenRegex, '');

    return speechDebugPrefix() || PREFERRED_PATTERN_ONLY
      ? hasPreferredName
      : isUsableInstance;
  };

// used in actions to create based on this...
export const getPreferredPrefix = () => {
  return speechDebugPrefix() || PREFERRED_PREFIX;
};

export const filterByLanguage = language => item => {
  // If no language(or universal) defined don't filter.
  if (!language || /xx/i.test(language)) {
    return true;
  }
  const shortLanguage = language.split('-')[0];
  if (item && item.language) {
    return item.language.toLowerCase().startsWith(shortLanguage?.toLowerCase());
  } else {
    // if no language key, filter out...
    return false;
  }
};

export const filterByNonDeprecatedModel = language => model => {
  const deprecatedModels = ['Narrowband', 'Broadband'];
  if (!!language && model?.name === language) {
    return true;
  }
  return (
    model &&
    model.name &&
    !deprecatedModels.some(name => {
      return model.name.indexOf(name) !== -1;
    })
  );
};

// Sort by browserLang
// Model1 = Lang(10), /shortForm/ 5, /Narrowband/ 1
// 100 110 111 001
//
const valueModel = (model, language) => {
  const dialect = navigator.language
    ? model.language.startsWith(navigator.language) * 1000
    : 0;
  const lang = model.language.startsWith(language) * 100;
  // Since regex.test returns boolean, +true becomes 1 and +false becomes 0
  const shortform = +/ShortForm/.test(model.name) * 10;
  const narrowband = +/Narrowband/.test(model.name) * 1;
  return dialect + lang + shortform + narrowband;
};

// Sort by browserLang
// Model1 = dialect(1000), Lang(100), /V3/ 10, /V2/ 1
// 100 110 111 001
//

const valueVoice = (voice, language) => {
  // Most often, language is just 'en', but browser lang
  // is more specific and contains a dialect, like the voices.
  // when Sorting, sort the browser dialect on top.
  const dialect = navigator.language
    ? voice.language.startsWith(navigator.language) * 10000
    : 0;
  const lang = voice.language.startsWith(language) * 1000;
  // Since regex.test returns boolean, +true becomes 1 and +false becomes 0
  const expressive = +/Expressive/.test(voice.name) * 100;
  const v3 = +/V3/.test(voice.name) * 10;
  const v2 = +/V2/.test(voice.name) * 1;
  return dialect + lang + expressive + v2 + v3;
};

export const modelSorter = language => (a, b) => {
  const value = valueModel(b, language) - valueModel(a, language);
  if (value === 0) {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
  }
  return value;
};

export const sortModels = (models, language = navigator.language) => {
  // Sort by BrowserLang, then ModelPreference
  return models.sort((a, b) => {
    const value = valueModel(b, language) - valueModel(a, language);
    if (value === 0) {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
    }
    return value;
  });
};

export const voiceSorter = language => (a, b) => {
  const value = valueVoice(b, language) - valueVoice(a, language);
  if (value === 0) {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
  }
  return value;
};

export const sortVoices = (voices, language = navigator.language) => {
  // Sort by BrowserLang, then ModelPreference
  return voices.sort((a, b) => {
    const value = valueVoice(b, language) - valueVoice(a, language);
    // Sort by the priorities in valueVoice first, then alphabetically if equal.
    if (value === 0) {
      if (a?.name < b?.name) {
        return -1;
      }
      if (a?.name > b?.name) {
        return 1;
      }
    }
    return value;
  });
};

export const getDefaultSTTModelByLanguage = (
  assistantLanguage: Language
): string => {
  switch (assistantLanguage) {
    case 'ar': // Arabic
      return DEFAULT_ARABIC_SPEECH_TO_TEXT_MODEL;
    case 'cs': // Czech
      return DEFAULT_CZECH_SPEECH_TO_TEXT_MODEL;
    case 'nl': // Dutch
      return DEFAULT_DUTCH_SPEECH_TO_TEXT_MODEL;
    case 'en': // English
      // Always return Large speech models in the case of en
      return DEFAULT_ENGLISH_SPEECH_TO_TEXT_MODEL_LSM;
    case 'fr': // French
      return DEFAULT_FRENCH_SPEECH_TO_TEXT_MODEL;
    case 'de': // German
      return DEFAULT_GERMAN_SPEECH_TO_TEXT_MODEL;
    case 'it': // Italian
      return DEFAULT_ITALIAN_SPEECH_TO_TEXT_MODEL;
    case 'ja': // Japanese
      return DEFAULT_JAPANESE_SPEECH_TO_TEXT_MODEL;
    case 'ko': // Korean
      return DEFAULT_KOREAN_SPEECH_TO_TEXT_MODEL;
    case 'es': // Spanish
      return DEFAULT_SPANISH_SPEECH_TO_TEXT_MODEL;
    default:
      return DEFAULT_ENGLISH_SPEECH_TO_TEXT_MODEL;
  }
};
